
import React, { useEffect, useRef } from 'react';

interface GridBackgroundProps {
  color?: string;
  isDense?: boolean;
}

const GridBackground: React.FC<GridBackgroundProps> = ({ 
  color = "rgba(59, 130, 246, 0.1)", 
  isDense = false 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
      drawGrid();
    };

    const drawGrid = () => {
      if (!ctx || !canvas) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Determine grid size based on device
      const gridSize = isDense ? 15 : 25;
      
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 0.5;

      // Draw vertical lines
      for (let x = 0; x <= canvas.width; x += gridSize) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
      }

      // Draw horizontal lines
      for (let y = 0; y <= canvas.height; y += gridSize) {
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
      }
      
      ctx.stroke();
    };

    // Initial setup
    resizeCanvas();
    
    // Handle resize and orientation change
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('orientationchange', resizeCanvas);
    
    // Handle document height changes by checking periodically
    const heightCheckInterval = setInterval(() => {
      if (canvas.height !== document.documentElement.scrollHeight) {
        resizeCanvas();
      }
    }, 1000);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('orientationchange', resizeCanvas);
      clearInterval(heightCheckInterval);
    };
  }, [color, isDense]);

  return (
    <canvas 
      ref={canvasRef} 
      className="fixed top-0 left-0 w-full h-full pointer-events-none -z-10" 
      aria-hidden="true"
    />
  );
};

export default GridBackground;
